import Clarity from '@microsoft/clarity';
import { getCookie } from 'helpers/cookie';
import { type HotjarUser, setHotjarUser } from 'hotjar';
import { type IUserDetails } from 'interfaces/user';
import { BehaviorSubject, type Observable, of } from 'rxjs';
import { map } from 'rxjs-operators';
import apiService from 'services/api';
import logService from 'services/log';
import { ACCOUNT_ENV, COOKIE_DOMAIN, EDUZZ_REGISTRY_VALID_KEY, IS_PROD } from 'settings';

import {
  type HyperflowToken,
  type LoginAsEmail,
  type SendHelpRequest,
  type SupportChatToken,
  type User
} from './types';

const ONE_YEARS_IN_SECONDS = 60 * 60 * 24 * 365;

export class UserService {
  private user$: BehaviorSubject<User>;
  private logout$: BehaviorSubject<() => void>;

  constructor() {
    this.user$ = new BehaviorSubject(null);
    this.logout$ = new BehaviorSubject(null);
  }

  public getUser(): Observable<User> {
    return this.user$.asObservable();
  }

  public setUser(user: User): Observable<User> {
    this.user$.next(user);
    const {
      cli_cod: id,
      user_email: email,
      user_name: name,
      belt,
      acpo_original_user_cli_cod,
      eduzz_registry_valid
    } = user;

    sessionStorage.setItem(EDUZZ_REGISTRY_VALID_KEY, String(eduzz_registry_valid));

    logService.setUser(user);
    setHotjarUser({
      id,
      email,
      name,
      belt,
      isAccessControl: id === acpo_original_user_cli_cod
    } as HotjarUser);

    if (IS_PROD) {
      Clarity.identify(id.toString());
    }

    const userCookie = getCookie('blk_user');

    if (!userCookie || (userCookie && userCookie !== user.id)) {
      document.cookie = `blk_user=${user?.id};domain=${COOKIE_DOMAIN};path=/;max-age=${ONE_YEARS_IN_SECONDS}`;
    }

    return of(user);
  }

  public setLogoutUser(action: () => void): Observable<() => void> {
    this.logout$.next(action);
    return of(action);
  }

  public logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    (window as any).Eduzz.Accounts.logout({
      ...ACCOUNT_ENV,
      redirectTo: window.location.origin
    });
  }

  public setMeAsProducer(data: any): Observable<any> {
    return apiService.post('/user/set_as_producer', data);
  }

  public hasUsedTour(data: any): Observable<any> {
    return apiService.put('/user/tour', data);
  }

  public loginByEventHash(data: any): Observable<any> {
    return apiService.post('/auth/loginbyeventhash', data).pipe(map(res => res.data[0]));
  }

  public loginAs(data: any): Observable<User> {
    return apiService.post('/user/login_as', data).pipe(map(res => res.data[0]));
  }

  public sendHelp(helpData: SendHelpRequest): Observable<any> {
    const { eventId, ...data } = helpData;
    return apiService.post(`help_services/helpsend/${eventId}`, data);
  }

  public loginAsEmail(email: string): Observable<LoginAsEmail> {
    return apiService.post('/user/acpo_login_as', { email }).pipe(map(res => res.data[0]));
  }

  public getDetails(): Observable<IUserDetails> {
    return apiService.get('/user/details').pipe(map(res => res.data));
  }

  public createSupportChatToken(ssid): Observable<SupportChatToken> {
    return apiService.post('/user/support-chat/create-token', { ssid });
  }

  public createHyperflowToken(userToHyperflow): Observable<HyperflowToken> {
    return apiService.post('/user/support-chat/create-hyperflow-token', { userToHyperflow });
  }

  public getBeltById(id: number): string {
    const belts = {
      100: 'Red Belt',
      101: 'Orange Belt',
      102: 'Green Belt',
      103: 'Black Belt',
      104: 'Golden Belt',
      109: 'Black Belt D2',
      110: 'Black Belt D3',
      111: 'Black Belt D4',
      112: 'Black Belt D5',
      113: 'Black Belt D6',
      114: 'Black Belt D7',
      115: 'Black Belt D8',
      116: 'Black Belt D9',
      300: 'Sensei',
      302: 'Golden Belt D20',
      303: 'Golden Belt D30',
      304: 'Golden Belt D40',
      305: 'Golden Belt D50',
      306: 'Golden Belt D60',
      307: 'Golden Belt D70',
      308: 'Golden Belt D80',
      309: 'Golden Belt D90'
    };

    return belts[id] || null;
  }
}

const userService = new UserService();
export default userService;
